import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import useNavigateToArtwork from "@/hooks/useNavigateToArtwork";
import useProtectActionUnregistered from "@/hooks/useProtectActionUnregistered";
import useStateHandler from "@/hooks/useStateHandler";
import useTranslations from "@/i18n/useTranslations";
import HomeCarouselAnalyticsService from "@/services/homeCarouselAnalytics.service";
import { HOME_AUTO_INITIAL_ARTWORK_LIMIT } from "@/shared/constants/homeCarousel";
import ArtworkSliderCard from "@/shared-components/cards/components/ArtworkSliderCard";
import UnregisteredPopup from "@/shared-components/popups/unregistered-user-popup";
import FHSlider from "@/shared-components/sliders/components/FHSlider";
import Heading from "@/shared-components/text/components/Heading";
import Text from "@/shared-components/text/components/Text";
import { isRestrictedArtwork } from "@/utils/artworkVisibility";

import useCarouselImpressionTracking from "./hooks/useCarouselImpressionTracking";
import DiscoverHeaderControls from "./DiscoverHeaderControls";

import "./styles/HomeSlider.css";

const shuffle = (items = []) => {
  const output = [...items];
  for (let index = output.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [output[index], output[randomIndex]] = [output[randomIndex], output[index]];
  }
  return output;
};

const isEligibleArtworkForHomeCarousel = (item) => (
  Boolean(item?.slug) && !isRestrictedArtwork(item)
);

export default function HomeLimitedEditions({
  artworks = [],
  title = "Limited Editions",
  contentMode = "manual",
  trackingContext = {},
}) {
  const tHome = useTranslations("common.shared.home");
  const navigate = useNavigate();
  const { state } = useStateHandler();
  const { privateActionHandlerUnregistered, showPopup } = useProtectActionUnregistered({
    typeOfProtection: "USER_LIMIT_VIEW",
  });
  const sliderRef = useRef(null);
  const impressionRef = useCarouselImpressionTracking(trackingContext);
  const [isScrolled, setIsScrolled] = useState(false);

  const normalizedMode = String(contentMode || "manual").toLowerCase();
  const isAutomaticMode = normalizedMode === "automatic";

  const displayedArtworks = useMemo(() => {
    const eligibleArtworks = Array.isArray(artworks)
      ? artworks.filter(isEligibleArtworkForHomeCarousel)
      : [];

    if (isAutomaticMode) {
      return shuffle(eligibleArtworks).slice(0, HOME_AUTO_INITIAL_ARTWORK_LIMIT);
    }

    return eligibleArtworks.slice(0, HOME_AUTO_INITIAL_ARTWORK_LIMIT);
  }, [isAutomaticMode, artworks]);

  const navigateToArtwork = useNavigateToArtwork(
    "home_limited_editions",
    { data: displayedArtworks, totalDocs: displayedArtworks.length },
    "/search/artwork?limitedEditions=true",
    { type: "home-carousel", label: title }
  );

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return undefined;

    const handleScroll = () => setIsScrolled(slider.scrollLeft > 0);
    slider.addEventListener("scroll", handleScroll);
    return () => slider.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSeeAll = () => {
    HomeCarouselAnalyticsService.trackHomeCarouselEvent(
      "carousel_see_all_click",
      {
        ...trackingContext,
        destination: "/limited-editions",
      },
      { userSession: state.user_session }
    );
    navigate("/limited-editions");
  };

  const handleProtectedClick = (event) => {
    privateActionHandlerUnregistered(event);
    return event?.defaultPrevented === true;
  };

  return (
    <>
      {showPopup ? <UnregisteredPopup showPopUp={showPopup} /> : null}
      <div ref={impressionRef} className="home-slider__container element-non-selectable">
        <div className="home-slider__header">
          <Heading className="home-title">{title}</Heading>
          <DiscoverHeaderControls viewportRef={sliderRef} />
        </div>

        <div className="full-bleed">
          <FHSlider
            ref={sliderRef}
            speed={1.4}
            className={`home-slider__slider ${isScrolled ? "scrolled" : ""}`}
            customPrevButton={<></>}
            customNextButton={<></>}
          >
            {displayedArtworks.length > 0 ? (
              displayedArtworks.map((item) => (
                <ArtworkSliderCard
                  key={`${title}-${item.slug || item._id || item.id}`}
                  artworkData={item}
                  imageClassName="home-slider__slider-item-image"
                  cardClassName="home-slider__slider-item always-show-text"
                  showLimitedEditionIcon
                  onClick={(event) => {
                    if (!state.user_session && handleProtectedClick(event)) {
                      return;
                    }
                    HomeCarouselAnalyticsService.trackHomeCarouselEvent(
                      "carousel_item_click",
                      {
                        ...trackingContext,
                        itemId: item._id || item.id || item.slug,
                        itemKind: "artwork",
                        destination: item.slug ? `/artwork/${item.slug}` : null,
                      },
                      { userSession: state.user_session }
                    );
                    navigateToArtwork(item.slug);
                  }}
                />
              ))
            ) : (
              <div className="home-slider__slider-item">
                <Text className="font-size-small">No limited editions available.</Text>
              </div>
            )}

            <div className="home-slider__slider-item see-all-slide">
              <Text
                className="home-slider__see-all font-size-medium"
                onClick={(event) => {
                  if (!state.user_session && handleProtectedClick(event)) {
                    return;
                  }
                  handleSeeAll();
                }}
              >
                {tHome("see_all")}
              </Text>
            </div>
          </FHSlider>
        </div>
      </div>
    </>
  );
}
